import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMap from "../common/GoogleMap";
import {
    filterForClusterData,
    mapViewChanged,
    toggleDiscoverModal,
} from "../../redux/slice/discoverSlice";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import { createPortal } from "react-dom";
import DiscoverCard from "../home/common/DiscoverCard";
import {
    CLUSTER_ANCHOR,
    CLUSTER_ICON_BASE,
    CLUSTER_SIZE,
    ICON_THRESHOLDS,
    OMS_SCRIPT_URL,
} from "../../utils/constants";

const MapView = ({ data = [], setMapRef, syncData }) => {
    const omsRef = useRef(null);
    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const dispatch = useDispatch();
    const clusterRef = useRef(null);
    const infoWindowRef = useRef(null);
    const idleListenerRef = useRef(null);
    const debouncedDispatchRef = useRef(null);
    const [mapReady, setMapReady] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const mapContainerRef = useRef(null);

    const mapOptions = useMemo(
        () => ({
            disableDefaultUI: true,
            zoomControl: false,
            minZoom: 3,
            maxZoom: 22,
            mapTypeId: "roadmap",
        }),
        []
    );

    const markerIcon = useMemo(() => {
        return {
            url:
                "data:image/svg+xml;charset=UTF-8," +
                encodeURIComponent(`
                <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#C400C6"/>
                </svg>
            `),
            scaledSize: { width: 32, height: 32 },
        };
    }, []);

    const getInfoWindow = () => {
        if (!window.google?.maps?.InfoWindow) {
            return null;
        }
        if (
            !infoWindowRef.current ||
            !infoWindowRef.current.infoWindow ||
            !infoWindowRef.current.container
        ) {
            infoWindowRef.current = {
                container: document.createElement("div"),
                infoWindow: new window.google.maps.InfoWindow(),
            };
        }
        return infoWindowRef.current;
    };

    const clearInfoWindow = () => {
        if (!infoWindowRef.current) return;
        infoWindowRef.current.infoWindow.close();
        infoWindowRef.current = null;
        setSelectedItem(null);
    };

    const clearMarkers = () => {
        clusterRef.current?.clearMarkers();
        markersRef.current.forEach((marker) => {
            omsRef.current?.removeMarker(marker);
            marker.setMap(null);
        });
        clusterRef.current = null;
        markersRef.current = [];
    };

    const handleGoogleApiLoaded = (map) => {
        if (!map || mapRef.current) return;

        mapRef.current = map;
        setMapRef(map);
        setMapReady(true);

        let lastZoom = map.getZoom();
        let lastBounds = map.getBounds();

        const dispatchMapViewChange = () => {
            const bounds = map.getBounds();
            if (!bounds) return;
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            dispatch(
                mapViewChanged({
                    mapZoom: map.getZoom(),
                    mapBound: {
                        ne: { lat: ne.lat(), lng: ne.lng() },
                        sw: { lat: sw.lat(), lng: sw.lng() },
                    },
                })
            );
        };

        debouncedDispatchRef.current = debounce(dispatchMapViewChange, 400);

        idleListenerRef.current = map.addListener("idle", () => {
            const zoom = map.getZoom();
            const bounds = map.getBounds();

            if (zoom !== lastZoom || (bounds && !bounds.equals(lastBounds))) {
                lastZoom = zoom;
                lastBounds = bounds;
                debouncedDispatchRef.current();
            }
        });
    };

    useEffect(() => {
        if (!mapReady || !window.google || omsRef.current) return;

        const script = document.createElement("script");
        script.src = OMS_SCRIPT_URL;
        script.async = true;

        script.onload = () => {
            omsRef.current = new window.OverlappingMarkerSpiderfier(
                mapRef.current,
                {
                    markersWontMove: true,
                    markersWontHide: true,
                    basicFormatEvents: true,
                    nearbyDistance: 0,
                    spiderfyDistanceMultiplier: 0,
                    animate: false,
                }
            );
        };

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
            omsRef.current = null;
        };
    }, [mapReady]);

    useEffect(() => {
        if (!mapReady || !window.google) return;

        clearMarkers();
        omsRef.current?.clearMarkers();
        if (!data?.length) return;

        const markers = data
            .filter((i) => i.latitude && i.longitude)
            .map((item) => {
                const marker = new window.google.maps.Marker({
                    position: {
                        lat: Number(item.latitude),
                        lng: Number(item.longitude),
                    },
                    icon: markerIcon,
                });
                marker.propertyId = item.id;
                marker.addListener("click", () => {
                    const info = getInfoWindow();
                    if (!info) return;

                    const { container, infoWindow } = info;

                    setSelectedItem(item);

                    mapContainerRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });

                    infoWindow.setContent(container);
                    infoWindow.open(mapRef.current, marker);

                    // window.google.maps.event.addListenerOnce(
                    //     infoWindow,
                    //     "domready",
                    //     () => {
                    //         const map = mapRef.current;
                    //         if (!map) return;

                    //         const infoHeight = container.offsetHeight || 300;

                    //         const EXTRA_PADDING = 250; //increase the number if your info window is cutting
                    //         const totalOffset = infoHeight + EXTRA_PADDING;

                    //         map.panBy(0, -totalOffset);
                    //     }
                    // );

                    window.google.maps.event.addListenerOnce(
                        infoWindow,
                        "domready",
                        () => {
                            const map = mapRef.current;
                            if (!map) return;

                            const infoEl = container;
                            const mapDiv = map.getDiv();

                            if (!infoEl || !mapDiv) return;

                            const infoRect = infoEl.getBoundingClientRect();
                            const mapRect = mapDiv.getBoundingClientRect();

                            // Check if top of InfoWindow is outside map container
                            const overflowTop = mapRect.top - infoRect.top;

                            if (overflowTop > 0) {
                                // Only pan the exact missing amount + small padding
                                const EXTRA_PADDING = 20;
                                map.panBy(0, -(overflowTop + EXTRA_PADDING));
                            }
                        }
                    );
                });
                omsRef.current?.addMarker(marker);
                return marker;
            });

        clusterRef.current = new MarkerClusterer({
            map: mapRef.current,
            markers,
            onClusterClick: (event, cluster, map) => {
                infoWindowRef.current?.infoWindow?.close();
                const propertyIds = cluster.markers.map((m) => m.propertyId);

                dispatch(filterForClusterData(propertyIds));
                dispatch(
                    toggleDiscoverModal({ isClusterPropertyListOpen: true })
                );

                //  map.fitBounds(cluster.bounds);
                const nextZoom = Math.min(map.getZoom() + 2, map.maxZoom || 20);
                map.setZoom(nextZoom);
                map.panTo(cluster.position);
            },
            renderer: {
                render({ markers, position }) {
                    const count = markers.length;

                    const icon =
                        ICON_THRESHOLDS.find((t) => count >= t.min)?.icon ||
                        "m1.png";

                    return new google.maps.Marker({
                        position,
                        icon: {
                            url: `${CLUSTER_ICON_BASE}${icon}`,
                            scaledSize: new google.maps.Size(
                                CLUSTER_SIZE,
                                CLUSTER_SIZE
                            ),
                            anchor: new google.maps.Point(
                                CLUSTER_ANCHOR,
                                CLUSTER_ANCHOR
                            ),
                        },
                        label: {
                            text: String(count),
                            color: "#fff",
                            fontSize: "16px",
                            fontWeight: "600",
                        },
                        zIndex: google.maps.Marker.MAX_ZINDEX + count,
                    });
                },
            },
        });

        markersRef.current = markers;
    }, [data, mapReady]);

    useEffect(() => {
        return () => {
            clearMarkers();
            clearInfoWindow();
            if (idleListenerRef.current) {
                window.google.maps.event.removeListener(
                    idleListenerRef.current
                );
            }
            debouncedDispatchRef.current?.cancel();
            dispatch(toggleDiscoverModal({ isClusterPropertyListOpen: false }));
        };
    }, []);

    return (
        <div className="map-view" ref={mapContainerRef}>
            {infoWindowRef.current &&
                selectedItem &&
                createPortal(
                    <ul className="map-info-property">
                        <button
                            className="map-info-close"
                            onClick={clearInfoWindow}
                        >
                            <img src="img/close-btn.svg" alt="Close" />
                        </button>
                        <DiscoverCard
                            ele={selectedItem}
                            syncData={
                                syncData?.filter(
                                    ({ property_id }) =>
                                        property_id ===
                                        Number(selectedItem?.property_id)
                                )?.[0]
                            }
                            view="grid"
                        />
                    </ul>,
                    infoWindowRef.current.container
                )}
            <GoogleMap
                zoom={11}
                width="100%"
                height="620px"
                createMapOptions={mapOptions}
                handleGoogleApiLoaded={handleGoogleApiLoaded}
            />
        </div>
    );
};

export default MapView;
